import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Plus, Camera, Trash2, X, Loader2, FileText, Calendar, Cloud, Users,
  ChevronDown, ChevronRight, Edit2, Save, Eye, ImagePlus,
} from 'lucide-react';
import {
  listComptesRendus, createCompteRendu, getCompteRendu, updateCompteRendu,
  deleteCompteRendu, uploadCRPhoto, crPhotoUrl, crPdfUrl,
} from '../services/api';
import PDFPreviewModal from './PDFPreviewModal';
import ScreenshotAnnotator from './ScreenshotAnnotator';

const LOTS_MAAG = [
  { id: '1', nom: "PILOTAGE DES INTERVENANTS DIRECTS / BUREAU D'ETUDE" },
  { id: '2', nom: 'GARDIENNAGE' },
  { id: '3', nom: 'PALISSADE' },
  { id: '4', nom: 'DEPOSE / DÉMOLITION' },
  { id: '5', nom: 'MAÇONNERIE' },
  { id: '6', nom: 'PROTECTION COUPE FEU' },
  { id: '7', nom: 'VITRINE FACADE HABILLAGE' },
  { id: '8', nom: 'SERRURERIE' },
  { id: '9', nom: 'DOUBLAGE - CLOISONNEMENT - FAUX-PLAFOND' },
  { id: '10', nom: 'PEINTURE' },
  { id: '11', nom: 'REVETEMENT DE SOL' },
  { id: '12', nom: 'ELECTRICITE' },
  { id: '13', nom: 'CLIMATISATION - RAC - VMC' },
  { id: '14', nom: 'PLOMBERIE SANITAIRES' },
  { id: '15', nom: 'MENUISERIE - POSE MOBILIER' },
  { id: '16', nom: 'SECURITE INCENDIE' },
  { id: '17', nom: 'NETTOYAGE' },
  { id: '18', nom: 'REMISE COMMERCIAL' },
];

const CATEGORIES = [
  { key: 'entreprises', label: 'Intervenants entreprises' },
  { key: 'moe', label: "Maîtrise d'œuvre" },
  { key: 'moa', label: "Maîtrise d'ouvrage / Intervenant direct" },
  { key: 'autres', label: 'Bureaux de contrôle / études / autres' },
];

const STATUTS = {
  present: { label: 'Présent', dot: 'bg-emerald-500' },
  absent: { label: 'Absent', dot: 'bg-amber-500' },
  excuse: { label: 'Excusé', dot: 'bg-blue-500' },
  absent_non_excuse: { label: 'Absent non excusé', dot: 'bg-red-500' },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const ComptesRendusTab = ({ projetId, projet }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await listComptesRendus(projetId);
      setItems(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projetId]); // eslint-disable-line

  const openPreview = (cr) => {
    setPreviewUrl(crPdfUrl(cr.id));
    setPreviewTitle(`Visite de chantier n°${cr.numero} — ${cr.date_visite}`);
  };

  const handleDelete = async (cr) => {
    if (!window.confirm(`Supprimer la visite n°${cr.numero} du ${cr.date_visite} ?`)) return;
    try {
      await deleteCompteRendu(cr.id);
      toast.success('Visite supprimée');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4" data-testid="comptes-rendus-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Comptes-rendus de visite</h3>
          <p className="text-xs text-slate-500">Documentez chaque visite avec photos et remarques par lot</p>
        </div>
        <button
          onClick={() => { setEditingId(null); setShowCreate(true); }}
          data-testid="add-cr-btn"
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 font-medium"
        >
          <Plus className="w-4 h-4" /> Nouvelle visite
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">Aucune visite enregistrée.</p>
          <p className="text-xs text-slate-400">Cliquez sur "Nouvelle visite" pour commencer.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((cr) => (
            <div key={cr.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition" data-testid={`cr-item-${cr.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bg-indigo-100 text-indigo-800 text-xs font-semibold px-2 py-0.5 rounded">N° {cr.numero}</span>
                    <h4 className="font-semibold text-slate-900">Visite du {cr.date_visite}</h4>
                    {cr.prochaine_visite && (
                      <span className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded">
                        📅 Prochaine : {cr.prochaine_visite} {cr.prochaine_visite_heure || ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span><Users className="w-3 h-3 inline mr-1" />{(cr.participants || []).length} participants</span>
                    <span>📝 {(cr.remarques || []).length} remarques</span>
                    <span>📷 {(cr.remarques || []).reduce((s, r) => s + ((r.photos || []).length), 0)} photos</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openPreview(cr)} data-testid={`cr-pdf-${cr.id}`} className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" title="Voir/Télécharger PDF">
                    <FileText className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setEditingId(cr.id); setShowCreate(true); }} className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title="Modifier">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(cr)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CompteRenduModal
          projetId={projetId}
          projet={projet}
          editingId={editingId}
          onClose={() => { setShowCreate(false); setEditingId(null); }}
          onSaved={(cr) => { setShowCreate(false); setEditingId(null); load(); openPreview(cr); }}
        />
      )}

      {previewUrl && (
        <PDFPreviewModal
          url={previewUrl}
          title={previewTitle}
          downloadFilename={`${previewTitle.replace(/[^a-z0-9 -]/gi, '').trim().replace(/\s+/g, '-')}.pdf`}
          onClose={() => setPreviewUrl(null)}
        />
      )}
    </div>
  );
};

// ===== Create/Edit modal =====
const CompteRenduModal = ({ projetId, projet, editingId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(!!editingId);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date_visite: todayISO(),
    prochaine_visite: '',
    prochaine_visite_heure: '',
    meteo: '',
    participants: [],
    remarques: [],
  });
  const [expandedLot, setExpandedLot] = useState('generales');

  useEffect(() => {
    if (editingId) {
      (async () => {
        try {
          const res = await getCompteRendu(editingId);
          const d = res.data;
          setForm({
            date_visite: d.date_visite || todayISO(),
            prochaine_visite: d.prochaine_visite || '',
            prochaine_visite_heure: d.prochaine_visite_heure || '',
            meteo: d.meteo || '',
            participants: d.participants || [],
            remarques: d.remarques || [],
          });
        } catch (e) {
          toast.error('Erreur chargement');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [editingId]);

  const addParticipant = (categorie) => {
    setForm((f) => ({
      ...f,
      participants: [...f.participants, { categorie, nom: '', entreprise: '', telephone: '', email: '', statut: 'present', convoque_prochain: true }],
    }));
  };

  const updateParticipant = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      participants: f.participants.map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }));
  };

  const removeParticipant = (idx) => {
    setForm((f) => ({ ...f, participants: f.participants.filter((_, i) => i !== idx) }));
  };

  const addRemarque = (lot_id) => {
    setForm((f) => ({
      ...f,
      remarques: [...f.remarques, { id: `tmp-${Date.now()}-${Math.random()}`, lot_id: lot_id || null, texte: '', photos: [], intervenant: '' }],
    }));
  };

  const updateRemarque = (rid, field, value) => {
    setForm((f) => ({
      ...f,
      remarques: f.remarques.map((r) => r.id === rid ? { ...r, [field]: value } : r),
    }));
  };

  const removeRemarque = (rid) => {
    setForm((f) => ({ ...f, remarques: f.remarques.filter((r) => r.id !== rid) }));
  };

  // Save first then upload photos (needs CR id)
  const ensureSaved = async () => {
    if (editingId) {
      await updateCompteRendu(editingId, form);
      return editingId;
    } else {
      const res = await createCompteRendu(projetId, form);
      return res.data.id;
    }
  };

  const handlePhotoForRemarque = async (rid, file, opts = {}) => {
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const id = await ensureSaved();
      const upRes = await uploadCRPhoto(id, file);
      const path = upRes.data.path;
      setForm((f) => ({
        ...f,
        remarques: f.remarques.map((r) => {
          if (r.id !== rid) return r;
          const cleaned = opts.replacePath
            ? (r.photos || []).filter((p) => p !== opts.replacePath)
            : (r.photos || []);
          return { ...r, photos: [...cleaned, path] };
        }),
      }));
      setTimeout(async () => {
        try {
          const upd = { ...form };
          upd.remarques = upd.remarques.map((r) => {
            if (r.id !== rid) return r;
            const cleaned = opts.replacePath
              ? (r.photos || []).filter((p) => p !== opts.replacePath)
              : (r.photos || []);
            return { ...r, photos: [...cleaned, path] };
          });
          await updateCompteRendu(id, upd);
        } catch (e) { /* silent */ }
      }, 200);
      toast.success(opts.replacePath ? 'Photo annotée enregistrée' : 'Photo ajoutée');
    } catch (e) {
      toast.error('Échec photo : ' + (e.response?.data?.detail || ''));
    }
  };

  const removePhotoFromRemarque = (rid, path) => {
    setForm((f) => ({
      ...f,
      remarques: f.remarques.map((r) => r.id === rid ? { ...r, photos: (r.photos || []).filter((p) => p !== path) } : r),
    }));
  };

  const handleSubmit = async () => {
    if (!form.date_visite) {
      toast.error('Date de visite requise');
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (editingId) {
        const res = await updateCompteRendu(editingId, form);
        saved = res.data;
      } else {
        const res = await createCompteRendu(projetId, form);
        saved = res.data;
      }
      toast.success('Visite enregistrée');
      onSaved(saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  const remarquesGenerales = form.remarques.filter((r) => !r.lot_id);
  const remarquesByLot = (lotId) => form.remarques.filter((r) => r.lot_id === lotId);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white flex items-center justify-between rounded-t-2xl">
          <h3 className="font-bold text-lg">
            {editingId ? 'Modifier la visite' : 'Nouvelle visite de chantier'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Date + prochaine visite */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Date de la visite *</label>
              <input type="date" value={form.date_visite} onChange={(e) => setForm({ ...form, date_visite: e.target.value })} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm" data-testid="cr-date-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">📅 Prochaine visite</label>
              <input type="date" value={form.prochaine_visite} onChange={(e) => setForm({ ...form, prochaine_visite: e.target.value })} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Heure</label>
              <input type="time" value={form.prochaine_visite_heure} onChange={(e) => setForm({ ...form, prochaine_visite_heure: e.target.value })} className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm" />
            </div>
          </div>

          {/* Participants */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Participants</h4>
            {CATEGORIES.map((cat) => {
              const group = form.participants.map((p, idx) => ({ p, idx })).filter(({ p }) => (p.categorie || 'entreprises') === cat.key);
              return (
                <div key={cat.key} className="mb-3 bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wide">{cat.label}</h5>
                    <button onClick={() => addParticipant(cat.key)} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Ajouter
                    </button>
                  </div>
                  {group.length === 0 ? (
                    <p className="text-[11px] text-slate-400 italic">Aucun participant</p>
                  ) : (
                    <div className="space-y-2">
                      {group.map(({ p, idx }) => (
                        <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-1.5 items-start bg-white p-2 rounded border border-slate-200">
                          <input className="md:col-span-3 h-8 px-2 border border-slate-300 rounded text-xs" placeholder="Nom" value={p.nom || ''} onChange={(e) => updateParticipant(idx, 'nom', e.target.value)} />
                          <input className="md:col-span-3 h-8 px-2 border border-slate-300 rounded text-xs" placeholder="Entreprise / Société" value={p.entreprise || ''} onChange={(e) => updateParticipant(idx, 'entreprise', e.target.value)} />
                          <input className="md:col-span-2 h-8 px-2 border border-slate-300 rounded text-xs" placeholder="Téléphone" value={p.telephone || ''} onChange={(e) => updateParticipant(idx, 'telephone', e.target.value)} />
                          <input className="md:col-span-2 h-8 px-2 border border-slate-300 rounded text-xs" placeholder="Email" value={p.email || ''} onChange={(e) => updateParticipant(idx, 'email', e.target.value)} />
                          <select className="md:col-span-1 h-8 px-1 border border-slate-300 rounded text-xs bg-white" value={p.statut || 'present'} onChange={(e) => updateParticipant(idx, 'statut', e.target.value)}>
                            <option value="present">Présent</option>
                            <option value="absent">Absent</option>
                            <option value="excuse">Excusé</option>
                            <option value="absent_non_excuse">Abs. non exc.</option>
                          </select>
                          <div className="md:col-span-1 flex items-center justify-between gap-1">
                            <label className="flex items-center gap-1 text-[10px] text-slate-600" title="Convoqué à la prochaine réunion">
                              <input type="checkbox" checked={p.convoque_prochain !== false} onChange={(e) => updateParticipant(idx, 'convoque_prochain', e.target.checked)} className="w-3 h-3" />
                              C
                            </label>
                            <button onClick={() => removeParticipant(idx)} className="p-1 text-slate-400 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Remarques par lot */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-2">Remarques</h4>

            {/* Generales */}
            <LotSection
              title="REMARQUES GÉNÉRALES"
              colorClass="bg-amber-50 border-amber-200"
              expanded={expandedLot === 'generales'}
              onToggle={() => setExpandedLot(expandedLot === 'generales' ? null : 'generales')}
              remarques={remarquesGenerales}
              onAdd={() => addRemarque(null)}
              onUpdate={updateRemarque}
              onRemove={removeRemarque}
              onPhoto={handlePhotoForRemarque}
              onRemovePhoto={removePhotoFromRemarque}
            />

            {LOTS_MAAG.map((lot) => {
              const rs = remarquesByLot(lot.id);
              return (
                <LotSection
                  key={lot.id}
                  title={`LOT ${lot.id} — ${lot.nom}`}
                  colorClass={rs.length > 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}
                  expanded={expandedLot === lot.id}
                  onToggle={() => setExpandedLot(expandedLot === lot.id ? null : lot.id)}
                  remarques={rs}
                  onAdd={() => addRemarque(lot.id)}
                  onUpdate={updateRemarque}
                  onRemove={removeRemarque}
                  onPhoto={handlePhotoForRemarque}
                  onRemovePhoto={removePhotoFromRemarque}
                />
              );
            })}
          </div>
        </div>

        <div className="sticky bottom-0 px-5 py-3 bg-white border-t border-slate-200 flex justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg">Annuler</button>
          <button onClick={handleSubmit} disabled={saving} data-testid="cr-save-btn" className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer & voir PDF
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Lot section (collapsible) =====
const LotSection = ({ title, colorClass, expanded, onToggle, remarques, onAdd, onUpdate, onRemove, onPhoto, onRemovePhoto }) => {
  const fileInputRef = useRef({});
  const [annotating, setAnnotating] = useState(null); // {rid, path}

  const handleAnnotationSave = async (rid, oldPath, blob) => {
    // Re-upload as new photo, then swap in remarque
    try {
      // Find the CR id via context — onPhoto handler does that work; reuse:
      const file = new File([blob], `annot-${Date.now()}.png`, { type: 'image/png' });
      // Use onPhoto's storage flow — we add as a NEW photo and remove the old one
      await onPhoto(rid, file, { replacePath: oldPath });
      setAnnotating(null);
    } catch (e) { /* silent */ }
  };
  return (
    <div className={`mb-2 border rounded-lg ${colorClass}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          <span className="text-xs font-bold text-slate-700 uppercase truncate">{title}</span>
          {remarques.length > 0 && (
            <span className="text-[10px] bg-white text-slate-700 px-1.5 py-0.5 rounded-full font-semibold">
              {remarques.length}
            </span>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onAdd(); }} className="text-[11px] text-indigo-700 hover:text-indigo-900 flex items-center gap-1 shrink-0">
          <Plus className="w-3 h-3" /> Ajouter
        </button>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {remarques.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic">Aucune remarque</p>
          ) : (
            remarques.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-2 space-y-1.5">
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    placeholder="Intervenant (optionnel)"
                    value={r.intervenant || ''}
                    onChange={(e) => onUpdate(r.id, 'intervenant', e.target.value)}
                    className="flex-1 h-8 px-2 border border-slate-300 rounded text-xs"
                  />
                  <button onClick={() => onRemove(r.id)} className="p-1 text-slate-400 hover:text-red-500" title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <textarea
                  rows={2}
                  placeholder="Décrivez la remarque..."
                  value={r.texte || ''}
                  onChange={(e) => onUpdate(r.id, 'texte', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                />
                {/* Photos */}
                <div className="flex flex-wrap gap-1.5 items-center">
                  {(r.photos || []).map((path) => (
                    <div key={path} className="relative w-16 h-16 rounded border border-slate-300 overflow-hidden group">
                      <img src={crPhotoUrl(path)} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => setAnnotating({ rid: r.id, path })}
                          className="bg-amber-500 hover:bg-amber-600 text-white rounded p-1"
                          title="Annoter"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => onRemovePhoto(r.id, path)}
                          className="bg-red-500 hover:bg-red-600 text-white rounded p-1"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current[r.id]?.click()}
                    className="w-16 h-16 rounded border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50 flex flex-col items-center justify-center"
                    data-testid={`cr-photo-add-${r.id}`}
                  >
                    <Camera className="w-4 h-4 text-slate-400" />
                    <span className="text-[9px] text-slate-500">Photo</span>
                  </button>
                  <input
                    ref={(el) => (fileInputRef.current[r.id] = el)}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onPhoto(r.id, f);
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {annotating && (
        <ScreenshotAnnotator
          src={crPhotoUrl(annotating.path)}
          onClose={() => setAnnotating(null)}
          onSave={(blob) => handleAnnotationSave(annotating.rid, annotating.path, blob)}
        />
      )}
    </div>
  );
};

export default ComptesRendusTab;
